import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useComments } from '@/hooks/useComments';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface CommentSheetProps {
  postId: string;
  dishName?: string;
  onClose: () => void;
}

const CommentSheet: React.FC<CommentSheetProps> = ({ postId, dishName, onClose }) => {
  const { comments, isLoading, postComment, isPosting } = useComments(postId);
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && !isPosting) {
      postComment(newComment.trim(), { 
        onSuccess: () => setNewComment('') 
      });
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[100] flex items-end justify-center"
        onClick={handleBackdropClick}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-lg bg-background rounded-t-2xl h-[80vh] flex flex-col"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-foreground">
              Comments {dishName && <span className="text-muted-foreground font-normal">on {dishName}</span>}
            </h3>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Comments List */}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
            {isLoading ? (
              // Loading skeletons
              <>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </div>
                ))}
              </>
            ) : comments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No comments yet</p>
                <p className="text-sm">Be the first to comment!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex items-start gap-3">
                  <Link to={`/profile/${comment.profiles?.id || comment.user_id}`}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.profiles?.avatar_url || undefined} />
                      <AvatarFallback className="bg-muted text-foreground text-xs">
                        {comment.profiles?.username?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <Link 
                        to={`/profile/${comment.profiles?.id || comment.user_id}`}
                        className="font-semibold text-sm text-foreground hover:underline"
                      >
                        @{comment.profiles?.username || 'Unknown'}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-foreground/90 break-words">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Comment Input */}
          <div className="shrink-0 bg-background border-t border-border" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {user ? (
              <form 
                onSubmit={handleSubmit}
                className="flex items-center gap-2 px-4 py-3"
              >
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1"
                  disabled={isPosting}
                />
                <Button 
                  type="submit" 
                  size="icon"
                  disabled={!newComment.trim() || isPosting}
                  className="shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <div className="px-4 py-3 text-center text-sm text-muted-foreground">
                Sign in to leave a comment
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommentSheet;
